import React, { useEffect, useState } from 'react';
import { FiPlusCircle } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { EssayTimeline } from '../essay-timeline';
import { EssayConsultSkill } from './essay-consult-skill';
import imgTia from '@/components/custom/tia/img/tia-avatar.png';
import lampIcon from '@/assets/images/icons/lamp-icon.png';
import { statusBadgeText } from '../utils/BadgeEssay';
import moment from 'moment';
import { Loader } from '@/components/custom/loader';
import heic2any from 'heic2any';
import { DashboardContent } from '@/layouts/dashboard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Widget, WidgetTitle } from '@/components/custom/widget';
import { CourseAboutBody, CourseAboutDescription, CourseAboutHeader, CourseAboutSubtitle, CourseAboutTitle } from '@/components/custom/course';
import { Badge } from '@/components/ui/badge';
import { SkillCardWrapper, SkillEvaluation, SkillEvaluationEssayHeader, SkillEvaluationEssayMark, SkillEvaluationEssayText, SkillEvaluationEssayTitle } from '@/components/custom/skill';
import { AvatarWrapper } from '@/components/ui/avatar';
import { EssayPage, EssayPageCardScore, EssayPageCardScoreValue, EssayPageMain, EssayPageSidebar, EssayPageSidebarSkill, EssayPageTia, EssayPageTiaContent, EssayPageTiaHeader } from '@/components/custom/essay';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FormSwitch } from '@/components/custom/form';

const MarkModalContentComponent = ({ content }) => {
  return (
    <>
      <WidgetTitle>{content.title}</WidgetTitle>
      <p className="mt-3">{content.body}</p>
    </>
  );
};

export function EssayConsult({ essayId, studentId, uri }) {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [studentEssay, setStudentEssay] = useState({});
  const client = useClient();
  const [parsedText, setParsedText] = useState(null);
  const [loading, setLoading] = useState(false);
  const [essay, setEssay] = useState({});
  const [openMarkModal, setOpenMarkModal] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [activeSubmission, setActiveSubmission] = useState(Number(studentId));
  const [markModalContent, setMarkModalContent] = useState(null);

  const fetchData = () => {
    setLoading(true);
    const essayFetch = client.fetch(['essays', essayId]);
    const submissionsFetch = client.fetch([`/essays/${essayId}/essay_students`]);
    const studentEssayFetch = client.fetch(`/essays/${essayId}/essay_students/${studentId}`);

    Promise.allSettled([essayFetch, submissionsFetch, studentEssayFetch]).then(([essayResponse, submissionsResponse, studentEssayResponse]) => {
      if (essayResponse.value.error) {
        toast.error('Erro ao buscar dados da redação');
      } else {
        setEssay(essayResponse.value.data);
      }

      if (submissionsResponse.value.error) {
        toast.error('Erro ao buscar envios da redação');
      } else {
        setSubmissions(submissionsResponse.value.data);
      }

      if (studentEssayResponse.value.error) {
        toast.error('Erro ao buscar redação enviada');
      } else {
        setStudentEssay(studentEssayResponse.value.data);
      }
      setLoading(false);
    });
  };

  const getStudentEssay = async () => {
    const { data, error } = await client.fetch(`/essays/${essayId}/essay_students/${studentId}`);
    if (error) {
      toast.error('Erro ao buscar redação');
    } else {
      setStudentEssay(data);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const convertHeicOrHeifToJpg = async imageUrl => {
    if (!imageUrl) {
      return null;
    }
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const convertedBlob = await heic2any({ blob });

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64data = reader.result;

        setStudentEssay(prevEssay => ({
          ...prevEssay,
          essay_image: { url: base64data }
        }));
      };
      reader.readAsDataURL(convertedBlob);
    } catch (error) {
      console.error('Erro ao converter imagem HEIC:', error);
    }
  };

  const switchDigital = useFormik({
    initialValues: {
      digital: true,
      competence: 'c1',
      points: 180
    }
  });

  useEffect(() => {
    if ((studentEssay && studentEssay?.status === 'annulled') || studentEssay?.status === 'annulled_by_teacher') {
      switchDigital.setFieldValue('digital', false);
    }

    if (studentEssay && studentEssay.essay_image && studentEssay.essay_image.url) {
      if (studentEssay.essay_image.url.includes('.heic') || studentEssay.essay_image.url.includes('.heif')) {
        convertHeicOrHeifToJpg(studentEssay.essay_image.url).then(url => {
          setStudentEssay(prevEssay => ({
            ...prevEssay,
            essay_image: { url }
          }));
        });
      }
    }
  }, [studentEssay]);

  useEffect(() => {
    getStudentEssay();
  }, [studentId]);

  const competencies = [
    { name: 'Competencia 1', description: 'Domínio da Norma Culta da Língua Portuguesa' },
    { name: 'Competencia 2', description: 'Compreensão e Aplicação Interdisciplinar do Tema Proposto' },
    { name: 'Competencia 3', description: 'Seleção e Organização Lógica de Argumentos' },
    { name: 'Competencia 4', description: 'Construção Coesa e Coerente da Argumentação' },
    { name: 'Competencia 5', description: 'Proposição de Intervenção Respeitosa aos Direitos Humanos' }
  ];

  useEffect(() => {
    renderTextWithMarks();
  }, [studentEssay]);

  const renderTextWithMarks = () => {
    const corrections = studentEssay?.corrections?.sort((a, b) => a.start_index - b.start_index);
    const tempElement = document.createElement('div');
    tempElement.innerHTML = studentEssay?.body;
    const text = tempElement.textContent;

    tempElement.remove();

    if (!corrections || corrections.length === 0) {
      setParsedText(<>{text}</>);
    } else {
      const selections = corrections.filter(correction => correction.start_index !== -1);
      const elements = [];
      let acumulator = '';
      let selectionIndex = 0;
      let counter = 0;

      text?.split('').forEach((c, i) => {
        if (i >= selections[selectionIndex]?.start_index + counter && i < selections[selectionIndex]?.end_index + counter) {
          acumulator += c;
        } else if (i === selections[selectionIndex]?.end_index + counter) {
          const currentIndex = selectionIndex;
          elements.push(
            <mark
              key={i}
              style={{ cursor: 'pointer' }}
              onClick={() => {
                setOpenMarkModal(true);
                setMarkModalContent({
                  title: 'Marcação',
                  body: selections[currentIndex].body
                });
              }}
            >
              {acumulator}
            </mark>
          );
          acumulator = '';
          elements.push(c);

          if (selectionIndex < selections.length - 1) {
            selectionIndex += 1;
          }
        } else {
          elements.push(c);
        }
      });

      if (acumulator) {
        elements.push(
          <mark
            key={text.length}
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setOpenMarkModal(true);
              setMarkModalContent({
                title: 'Marcação',
                body: selections[selectionIndex].body
              });
            }}
          >
            {acumulator}
          </mark>
        );
      }

      setParsedText(<>{elements}</>);
    }
  };

  const breadcrumbs = {
    title: 'Atividades',
    nav: [
      {
        route: '/redacao',
        name: 'Redação',
        isActive: false
      },
      {
        route: uri,
        name: essay.topic,
        isActive: true
      }
    ]
  };

  const checkScore = score => {
    if (score <= 333) {
      return 'danger';
    } else if (score > 333 && score <= 666) {
      return 'warning';
    } else if (score > 666) {
      return 'success';
    }
  };

  const validSubmissions = submissions.filter(item => item.status !== 'minimum_length_not_satisfied');

  const statusBadgeColor = {
    accepting_proposal: 'attention',
    close_for_submissions: 'neutral',
    processing: 'warning',
    unreviewed: 'success',
    reviewed: 'success',
    annulled: 'danger',
    annulled_by_teacher: 'danger ',
    minimum_length_not_satisfied: 'danger',
    failed_to_process: 'danger',
    reviewing: 'warning',
    not_sent: 'neutral',
    ocr_review: 'warning',
    sending_by_teacher: 'warning'
  };

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <Tabs defaultValue="correcao">
        {(statusBadgeText[studentEssay?.status] === 'Correção por Professor' || statusBadgeText[studentEssay?.status] === 'Correção por IA' || statusBadgeText[studentEssay?.status] === 'Anulada') && (
          <TabsList className="mb-7">
            <TabsTrigger value="correcao">Correção</TabsTrigger>
            <TabsTrigger value="competencias">Competências</TabsTrigger>
            <TabsTrigger value="sobre">Sobre</TabsTrigger>
          </TabsList>
        )}

        {loading ? (
          <Loader />
        ) : (
          <>
            <TabsContent value="correcao">
              <EssayPage className={studentEssay?.status === 'annulled' || studentEssay?.status === 'annulled_by_teacher' ? 'justify-start' : ''}>
                <EssayPageSidebar>
                  {essay?.['allow-multiple-submissions'] ? (
                    <Widget>
                      {/* Aguardar definição da regra de quantidade de envios */}
                      {validSubmissions.length < 2 && (
                        <div className="border-b border-solid border-[#ECE9F8]">
                          <Button
                            variant="outline"
                            size="sm"
                            fullWidth
                            className="mb-6"
                            onClick={() => navigate(`/redacao/enviar/${essayId}`)}
                          >
                            <FiPlusCircle />
                            Novo envio
                          </Button>
                        </div>
                      )}

                      <EssayTimeline
                        submissions={submissions}
                        activeSubmission={activeSubmission}
                        setActiveSubmission={setActiveSubmission}
                        essayId={essayId}
                      />
                    </Widget>
                  ) : (
                    <Widget>
                      <EssayPageCardScore>
                        <EssayPageCardScoreValue variant={checkScore(studentEssay.grade)}>{studentEssay.grade}</EssayPageCardScoreValue>
                        <span>pontos</span>
                      </EssayPageCardScore>
                      <div className="flex items-center justify-between">
                        <Badge
                          variant={statusBadgeColor[essay?.status]}
                          size="xs"
                        >
                          {statusBadgeText[essay?.status]}
                        </Badge>
                        <p className="mb-0 text-xs text-neutral-light">em {moment(studentEssay?.['delivery_date']).format('DD/MM/YYYY')}</p>
                      </div>

                      {studentEssay?.status === 'annulled' || studentEssay?.status === 'annulled_by_teacher' ? (
                        <div className="mt-6 flex items-center justify-between">
                          <h2 className="mb-0">Avaliação ortográfica</h2>

                          <Badge
                            square
                            size="sm"
                            variant={studentEssay?.['grammar_quality'] === 'Boa' ? 'success' : studentEssay?.['grammar_quality'] === 'Mediana' ? 'warning' : 'danger'}
                          >
                            {studentEssay?.['grammar_quality']}
                          </Badge>
                        </div>
                      ) : null}
                    </Widget>
                  )}
                </EssayPageSidebar>

                <EssayPageMain>
                  <EssayPageTia>
                    <AvatarWrapper
                      className="size-14"
                      src={imgTia}
                      text="T.I.A"
                    />
                    <EssayPageTiaContent>
                      <EssayPageTiaHeader>
                        <h2>Sugestões da T.I.A para melhorar sua redação</h2>
                        <img
                          src={lampIcon}
                          className="h-auto w-4 shrink-0"
                        />
                      </EssayPageTiaHeader>
                      <p className="mb-4">{studentEssay?.['suggestion_to_improve']}</p>
                    </EssayPageTiaContent>
                  </EssayPageTia>

                  <SkillEvaluation>
                    <SkillEvaluationEssayHeader>
                      <SkillEvaluationEssayTitle>Sua redação</SkillEvaluationEssayTitle>
                      {studentEssay?.status !== 'annulled' || studentEssay?.status !== 'annulled_by_teacher' ? (
                        <FormSwitch
                          label={switchDigital.values.digital ? 'Digital' : 'Cursiva'}
                          checked={switchDigital.values.digital}
                          onCheckedChange={checked => {
                            switchDigital.setFieldValue('digital', checked);
                          }}
                        />
                      ) : null}
                    </SkillEvaluationEssayHeader>

                    {switchDigital?.values?.digital ? (
                      <SkillEvaluationEssayText className="overflow-visible">
                        <SkillEvaluationEssayMark className="whitespace-pre-line"> {parsedText} </SkillEvaluationEssayMark>
                      </SkillEvaluationEssayText>
                    ) : (
                      <>
                        <img
                          className="cursor-pointer"
                          src={studentEssay?.essay_image?.url}
                          alt="prova digitalizada"
                          onClick={() => setShow(true)}
                          style={{ objectFit: 'contain' }}
                        />

                        <Dialog
                          open={show}
                          onOpenChange={() => setShow(false)}
                        >
                          <DialogContent size="lg">
                            <DialogTitle className="sr-only">Prova digitalizada</DialogTitle>
                            <img
                              src={studentEssay?.essay_image?.url}
                              alt="prova digitalizada"
                            />
                          </DialogContent>
                        </Dialog>
                      </>
                    )}
                  </SkillEvaluation>
                </EssayPageMain>

                {studentEssay?.status !== 'annulled' || studentEssay?.status !== 'annulled_by_teacher' ? (
                  <EssayPageSidebarSkill>
                    {studentEssay?.['grammar_quality'] && (
                      <Widget className="mb-6">
                        <WidgetTitle className="mb-2">Avaliação ortográfica</WidgetTitle>

                        <Badge
                          square
                          variant={studentEssay?.['grammar_quality'] === 'Boa' ? 'success' : studentEssay?.['grammar_quality'] === 'Mediana' ? 'warning' : 'danger'}
                        >
                          {studentEssay?.['grammar_quality']}
                        </Badge>
                      </Widget>
                    )}

                    <Widget>
                      <WidgetTitle className="mb-4">Avaliação de competências</WidgetTitle>

                      <SkillCardWrapper>
                        {studentEssay?.competencies?.map((competence, i) => (
                          <EssayConsultSkill
                            competence={competence}
                            item={competencies[i]}
                            key={i}
                          />
                        ))}
                      </SkillCardWrapper>
                    </Widget>
                  </EssayPageSidebarSkill>
                ) : null}

                <Dialog
                  open={openMarkModal}
                  onOpenChange={() => setOpenMarkModal(false)}
                >
                  <DialogContent size="md">
                    <DialogTitle className="sr-only">Marcação</DialogTitle>
                    <MarkModalContentComponent content={markModalContent} />
                  </DialogContent>
                </Dialog>
              </EssayPage>
            </TabsContent>

            <TabsContent value="sobre">
              <Widget className="flex flex-col gap-8">
                <CourseAboutHeader>
                  <CourseAboutTitle>Enunciado</CourseAboutTitle>

                  <CourseAboutDescription className="whitespace-pre-wrap">{essay?.statement}</CourseAboutDescription>

                  <CourseAboutDescription>
                    Tema: {essay?.topic}
                    <br />
                    {/* Título: <strong>{essay?.title}</strong> */}
                  </CourseAboutDescription>
                </CourseAboutHeader>

                <CourseAboutBody>
                  <CourseAboutTitle>Textos motivadores</CourseAboutTitle>

                  {essay?.['essay-motivator-texts']?.map((item, index) => {
                    return (
                      <article key={index}>
                        <CourseAboutSubtitle>Texto {index + 1}</CourseAboutSubtitle>
                        <CourseAboutDescription dangerouslySetInnerHTML={{ __html: item.body }} />
                        {/* <CourseAboutDescription>Disponível em: https://brasilescola.uol.com.br/portugues/preconceito-linguistico.htm. Acesso em: 19 ago. 2022 (adaptado).</CourseAboutDescription> */}
                      </article>
                    );
                  })}
                </CourseAboutBody>
              </Widget>
            </TabsContent>

            <TabsContent value="competencias">
              <div>
                <Widget className="mb-6 w-[300px]">
                  <WidgetTitle className="mb-2">Avaliação ortográfica</WidgetTitle>
                  <Badge
                    square
                    variant="success"
                  >
                    Boa
                  </Badge>
                </Widget>

                <Widget>
                  <WidgetTitle className="mb-4">Avaliação de competências</WidgetTitle>

                  <SkillCardWrapper>
                    {studentEssay?.competencies?.map((competence, i) => (
                      <EssayConsultSkill
                        competence={competence}
                        item={competencies[i]}
                        key={i}
                      />
                    ))}
                  </SkillCardWrapper>
                </Widget>
              </div>
            </TabsContent>
          </>
        )}
      </Tabs>
    </DashboardContent>
  );
}
